import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Target, Loader2 } from 'lucide-react';
import { atsAPI } from '@/lib/api';
import { toast } from 'sonner';

const ATSCheckButton = ({ resumeContent, resumeId, buttonText = "Run ATS Check", variant = "default", size = "default", className = "" }) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRunCheck = async () => {
    if (!resumeContent) {
      toast.error('No resume content to check');
      return;
    }

    setLoading(true);
    try {
      const response = await atsAPI.runCheck({
        resume_content: resumeContent,
        resume_id: resumeId,
        job_description: jobDescription || undefined
      });
      
      toast.success('ATS check complete!');
      setShowDialog(false);
      navigate(`/ats-report/${response.data.id}`);
    } catch (error) {
      // Show detailed error message from backend if available
      const errorMessage = error.response?.data?.detail || 'Failed to run ATS check. Please try again.';
      toast.error(errorMessage, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        variant={variant}
        size={size}
        className={className}
      >
        <Target className="w-4 h-4 mr-2" />
        {buttonText}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Target className="w-6 h-6 text-sky-600" />
              <span>Run ATS Compatibility Check</span>
            </DialogTitle>
            <DialogDescription>
              Analyze how Applicant Tracking Systems (ATS) will read your resume. 
              Optionally paste a job description for a targeted match analysis.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="job-description">Job Description (Optional)</Label>
              <Textarea
                id="job-description"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the job description here to check how well your resume matches..."
                rows={6}
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-2">
                Adding a job description will provide a detailed keyword match analysis
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRunCheck}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running Check...
                  </>
                ) : (
                  'Run ATS Check'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ATSCheckButton;
